import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { identifyUser, resetAnalytics } from "@/lib/analytics";

export type LocalCompany = {
	id: number;
	name: string;
	registration_country: string | null;
};

export type LocalUser = {
	id: number;
	auth0_sub: string;
	email: string;
	email_verified: boolean;
	name: string | null;
	picture_url: string | null;
	auth_provider: string | null;
	last_login_at: string | null;
	created_at: string;
	updated_at: string;
	company_id: number | null;
	company: LocalCompany | null;
	designation: string | null;
	contact_country_code: string | null;
	contact_number: string | null;
};

type AuthMethod = "password" | "auth0-spa";

type AuthStore = {
	accessToken: string | null;
	refreshToken: string | null;
	authMethod: AuthMethod | null;
	user: LocalUser | null;
	pendingEmail: string | null;
	emailVerificationRequired: boolean;
	setTokens: (input: {
		accessToken: string | null;
		refreshToken?: string | null;
		authMethod: AuthMethod | null;
	}) => void;
	setUser: (user: LocalUser | null) => void;
	setPendingVerification: (email: string | null) => void;
	reset: () => void;
};

export const useAuthStore = create<AuthStore>()(
	persist(
		(set) => ({
			accessToken: null,
			refreshToken: null,
			authMethod: null,
			user: null,
			pendingEmail: null,
			emailVerificationRequired: false,
			setTokens: ({ accessToken, refreshToken, authMethod }) =>
				set((state) => {
					const nextTokenSubject = accessToken
						? getTokenSubject(accessToken)
						: null;
					const currentTokenSubject = state.accessToken
						? getTokenSubject(state.accessToken)
						: null;
					const tokenChanged = accessToken !== state.accessToken;
					const userMatchesNextToken =
						nextTokenSubject != null &&
						state.user?.auth0_sub === nextTokenSubject;
					const tokenRotationForSameSubject =
						nextTokenSubject != null &&
						currentTokenSubject != null &&
						nextTokenSubject === currentTokenSubject;
					const shouldClearUser = tokenChanged && !userMatchesNextToken;
					const shouldClearAuthMetadata =
						shouldClearUser && !tokenRotationForSameSubject;

					return {
						accessToken,
						refreshToken:
							refreshToken === undefined ? state.refreshToken : refreshToken,
						authMethod,
						user: shouldClearUser ? null : state.user,
						pendingEmail: shouldClearAuthMetadata ? null : state.pendingEmail,
						emailVerificationRequired: shouldClearAuthMetadata
							? false
							: state.emailVerificationRequired,
					};
				}),
			setUser: (user) =>
				set((state) => {
					const tokenSubject = state.accessToken
						? getTokenSubject(state.accessToken)
						: null;
					const userMatchesToken =
						user == null ||
						tokenSubject == null ||
						user.auth0_sub === tokenSubject;

					if (!userMatchesToken) {
						return state;
					}

					if (user) identifyUser(user);

					return {
						user,
						emailVerificationRequired: false,
						pendingEmail: user?.email ?? null,
					};
				}),
			setPendingVerification: (email) =>
				set({
					pendingEmail: email,
					emailVerificationRequired: true,
					user: null,
				}),
			reset: () => {
				resetAnalytics();
				set({
					accessToken: null,
					refreshToken: null,
					authMethod: null,
					user: null,
					pendingEmail: null,
					emailVerificationRequired: false,
				});
			},
		}),
		{
			name: "marinavalue-auth",
			storage: createJSONStorage(() => localStorage),
		},
	),
);

export const isTokenExpired = (token: string): boolean => {
	try {
		const parsed = decodeAuth0TokenPayload(token);
		const exp = parsed.exp;
		if (!exp) return true;
		const bufferSeconds = 60;
		return Date.now() >= (exp - bufferSeconds) * 1000;
	} catch {
		return true;
	}
};

// This local decode is only for store consistency checks; the backend still
// validates token signatures and permissions before returning protected data.
export const getTokenSubject = (token: string): string | null => {
	try {
		return decodeAuth0TokenPayload(token).sub ?? null;
	} catch {
		return null;
	}
};

type Auth0TokenPayload = {
	exp?: number;
	sub?: string;
};

function decodeAuth0TokenPayload(token: string): Auth0TokenPayload {
	const payload = token.split(".")[1];
	if (!payload) throw new Error("Missing JWT payload");
	const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
	const padded = normalized.padEnd(
		normalized.length + ((4 - (normalized.length % 4)) % 4),
		"=",
	);
	return JSON.parse(window.atob(padded)) as Auth0TokenPayload;
}
